import { Card, Stack } from "@salesapp/ui";
import { formatUtc } from "./format";

export interface TrialEmailPlanStep {
  step_key: string;
  track: "standard" | "dormant";
  label: string;
  day: number;
  scheduled_for: string | null;
  status: "sent" | "purchase" | "manual" | "skipped" | "stopped" | "held" | "not_used" | "not_sent" | "next" | "upcoming";
  sent_at: string | null;
  recipients: { email: string; sent_at: string | null }[];
}

export interface TrialEmailPlanData {
  track: "standard" | "dormant" | "stopped";
  engagement: { state: string; logins: number; last_login_at: string | null; records: number } | null;
  days_elapsed: number;
  counter: { position: number; total: number; mailed: number };
  next: (TrialEmailPlanStep & { due_now: boolean }) | null;
  steps: TrialEmailPlanStep[];
}

const STATUS_TEXT: Record<TrialEmailPlanStep["status"], string> = {
  sent: "Sent",
  purchase: "Skipped (purchased)",
  manual: "Marked sent by staff",
  skipped: "Skipped (day already passed)",
  stopped: "Not needed (paid)",
  held: "Held while org is dormant",
  not_used: "Not used (org is not dormant)",
  not_sent: "Not sent",
  next: "Next",
  upcoming: "Upcoming",
};

const TRACK_TEXT = {
  standard: "Standard sequence: the org is using the app, so it gets the normal follow-up letters.",
  dormant:
    "Re-engagement track: the org has not started using the app, so it gets the short nudge letters instead of the day 10 and 20 check-ins.",
  stopped: "Sequence stopped: the customer is paying, so no more trial letters are sent.",
} as const;

function when(step: TrialEmailPlanStep): string {
  return step.sent_at ? formatUtc(step.sent_at) : step.scheduled_for ? `due ${formatUtc(step.scheduled_for)}` : "—";
}

export function TrialEmailPlan({ plan }: { plan: TrialEmailPlanData }) {
  const { counter, next, engagement } = plan;
  const visible = plan.steps.filter((step) => plan.track === "dormant" || step.track === "standard");
  const dormantRows = plan.track === "dormant" ? [] : plan.steps.filter((step) => step.track === "dormant" && step.sent_at);
  return (
    <Card>
      <Stack gap={3}>
        <strong>Trial email progress</strong>
        <p className="admin-muted">{TRACK_TEXT[plan.track]}</p>
        {plan.track !== "stopped" ? (
          <p data-testid="trial-email-counter">
            Step {counter.position} of {counter.total} · {counter.mailed} email{counter.mailed === 1 ? "" : "s"} sent ·
            day {plan.days_elapsed} of the trial
            {engagement ? (
              <>
                {" "}
                · usage: <span className={`admin-badge is-${engagement.state}`}>{engagement.state}</span>{" "}
                <span className="admin-muted">
                  ({engagement.logins} sign-in{engagement.logins === 1 ? "" : "s"}, {engagement.records} record
                  {engagement.records === 1 ? "" : "s"})
                </span>
              </>
            ) : null}
          </p>
        ) : null}
        {plan.track !== "stopped" ? (
          <p data-testid="trial-email-next">
            <strong>Next: </strong>
            {next
              ? `${next.label} — ${next.due_now ? "due now, goes out on the next worker run (within 15 minutes)" : formatUtc(next.scheduled_for)}`
              : "nothing left to send."}
          </p>
        ) : null}
        <div className="admin-nurture-table">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Day</th>
                <th>Status</th>
                <th>Sent / due (UTC)</th>
                <th>Sent to</th>
              </tr>
            </thead>
            <tbody>
              {[...visible, ...dormantRows].map((step) => (
                <tr key={step.step_key}>
                  <td>{step.label}</td>
                  <td>{step.day}</td>
                  <td>{STATUS_TEXT[step.status]}</td>
                  <td>{step.status === "not_used" || step.status === "not_sent" ? "—" : when(step)}</td>
                  <td>
                    {step.recipients.length
                      ? step.recipients.map((r) => (
                          <div key={`${r.email}-${r.sent_at}`}>
                            {r.email}
                            {r.sent_at ? <span className="admin-muted"> · {formatUtc(r.sent_at)}</span> : null}
                          </div>
                        ))
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <details>
          <summary className="admin-muted">How these emails work</summary>
          <ul className="admin-muted">
            <li>The worker checks every 15 minutes. Each trial org gets at most one email per run, sent to every active org admin (not to regular users).</li>
            <li>Standard sequence: Welcome at signup, then Trial Mail 01–05 on days 10, 20, 25, 28 and 30 (editable below).</li>
            <li>
              Re-engagement track: while an org has added no records and has signed in once, or not for 4 days, it gets
              letters on days 2, 4, 7, 10, 14, 18 and 22, at least 48 hours apart. If several days have passed it gets
              the latest one only.
            </li>
            <li>Dormant orgs skip the day 10 and 20 check-ins but always get the day 25, 28 and 30 expiry reminders.</li>
            <li>The re-engagement letters stop as soon as the org adds an opportunity or starts working.</li>
            <li>A purchase ticks every box so a paying customer is never mailed again. Unticking a box lets that letter go out again.</li>
            <li>Ticking a box by hand only stops that email. It does not send anything.</li>
          </ul>
        </details>
      </Stack>
    </Card>
  );
}
